import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Book, Cross, Church, Heart, Star, Users } from 'lucide-react';

export default function ScriptureReferences() {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen p-3 sm:p-4 md:p-6 relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <Button
          onClick={() => navigate('/start')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Game
        </Button>

        <Card className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8">
            <div className="p-2 sm:p-3 md:p-4 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl sm:rounded-2xl flex-shrink-0">
              <Book className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#10B981] to-[#06B6D4] bg-clip-text text-transparent leading-tight">
              Scripture References & Topics
            </h1>
          </div>

          <div className="prose prose-sm sm:prose-base max-w-none space-y-6 sm:space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                The Mega Church Game draws questions from across the entire Bible, covering key stories, figures, 
                events, and teachings. This comprehensive guide organizes the biblical content by topic and level, 
                helping you know what to study for each stage of the game. Use this as a study companion to improve 
                your scores and deepen your scriptural knowledge.
              </p>
            </section>

            {/* Old Testament Topics */}
            <section className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Book className="w-6 h-6 text-amber-600" />
                <h2 className="text-3xl font-bold text-gray-800">Old Testament Topics</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-amber-700 mb-3">Creation & Early History</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Key Topics:</strong> Creation account, Adam and Eve, Cain and Abel, Noah's Ark, Tower of Babel</p>
                    <p><strong>Important Verses:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Genesis 1:1 - "In the beginning God created the heavens and the earth"</li>
                      <li>Genesis 1:27 - Humanity created in God's image</li>
                      <li>Genesis 6-9 - The Flood narrative</li>
                      <li>Genesis 11 - Tower of Babel</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 1-2</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-orange-700 mb-3">The Patriarchs</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Key Figures:</strong> Abraham, Isaac, Jacob, Joseph</p>
                    <p><strong>Major Stories:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Abraham's call and covenant (Genesis 12-25)</li>
                      <li>Isaac's near-sacrifice (Genesis 22)</li>
                      <li>Jacob's ladder and wrestling with God (Genesis 28, 32)</li>
                      <li>Joseph in Egypt (Genesis 37-50)</li>
                    </ul>
                    <p><strong>Key Verse:</strong> Genesis 12:2-3 - God's promise to Abraham</p>
                    <p className="text-sm italic">Game Coverage: Level 1-3</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-red-700 mb-3">Moses & The Exodus</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Key Events:</strong> Birth of Moses, burning bush, ten plagues, Red Sea crossing, Ten Commandments</p>
                    <p><strong>Important References:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Exodus 3:14 - God reveals His name ("I AM WHO I AM")</li>
                      <li>Exodus 7-12 - The ten plagues</li>
                      <li>Exodus 14 - Parting of the Red Sea</li>
                      <li>Exodus 20 - The Ten Commandments</li>
                      <li>Exodus 32 - The golden calf incident</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 1-4</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-purple-700 mb-3">Judges & Kings</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Key Figures:</strong> Samson, Gideon, Deborah, Samuel, Saul, David, Solomon</p>
                    <p><strong>Major Stories:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Judges - Cycle of rebellion and deliverance</li>
                      <li>1 Samuel 16 - David's anointing</li>
                      <li>1 Samuel 17 - David and Goliath</li>
                      <li>2 Samuel 11 - David and Bathsheba</li>
                      <li>1 Kings 3 - Solomon's wisdom</li>
                      <li>1 Kings 18 - Elijah and prophets of Baal</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 2-5</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-blue-700 mb-3">Prophets & Wisdom Literature</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Major Prophets:</strong> Isaiah, Jeremiah, Ezekiel, Daniel</p>
                    <p><strong>Minor Prophets:</strong> Hosea, Joel, Amos, Jonah, Micah, Habakkuk, Malachi (and 5 others)</p>
                    <p><strong>Wisdom Books:</strong> Job, Psalms, Proverbs, Ecclesiastes, Song of Solomon</p>
                    <p><strong>Key Verses:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Psalm 23 - The Lord is my shepherd</li>
                      <li>Proverbs 3:5-6 - Trust in the Lord with all your heart</li>
                      <li>Isaiah 53 - Prophecy of the suffering servant</li>
                      <li>Jeremiah 29:11 - Plans to prosper you</li>
                      <li>Daniel 6 - Daniel in the lion's den</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 3-6</p>
                  </div>
                </div>
              </div>
            </section>

            {/* New Testament Topics */}
            <section className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Cross className="w-6 h-6 text-blue-600" />
                <h2 className="text-3xl font-bold text-gray-800">New Testament Topics</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-blue-700 mb-3">Life & Ministry of Jesus</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Birth & Early Life:</strong> Bethlehem birth, shepherds, wise men, escape to Egypt, boy Jesus at temple</p>
                    <p><strong>Ministry Highlights:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Baptism by John (Matthew 3)</li>
                      <li>Temptation in wilderness (Matthew 4)</li>
                      <li>Calling of the twelve disciples</li>
                      <li>Sermon on the Mount (Matthew 5-7)</li>
                      <li>Parables (Prodigal Son, Good Samaritan, Sower, etc.)</li>
                      <li>Miracles (water to wine, feeding 5000, walking on water, raising Lazarus)</li>
                    </ul>
                    <p><strong>Key Verses:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>John 3:16 - For God so loved the world</li>
                      <li>Matthew 5:3-12 - The Beatitudes</li>
                      <li>Matthew 28:19-20 - The Great Commission</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 1-4</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-red-700 mb-3">Crucifixion & Resurrection</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Passion Week:</strong> Triumphal entry, Last Supper, Garden of Gethsemane, betrayal, trials</p>
                    <p><strong>The Cross:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Crucifixion between two thieves</li>
                      <li>Jesus' seven last words from the cross</li>
                      <li>Darkness over the land</li>
                      <li>Tearing of the temple veil</li>
                      <li>Death and burial</li>
                    </ul>
                    <p><strong>Resurrection:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Empty tomb on the third day</li>
                      <li>Appearances to Mary Magdalene, disciples, and 500+</li>
                      <li>Thomas's doubt and belief</li>
                      <li>Ascension to heaven</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 2-5</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-green-700 mb-3">Early Church & Acts</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Key Events:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Acts 2 - Pentecost and birth of the church</li>
                      <li>Acts 9 - Conversion of Saul/Paul</li>
                      <li>Acts 10 - Peter's vision and Cornelius</li>
                      <li>Paul's missionary journeys</li>
                      <li>Council of Jerusalem (Acts 15)</li>
                      <li>Paul's arrest and journey to Rome</li>
                    </ul>
                    <p><strong>Key Figures:</strong> Peter, Paul, Stephen, Philip, Barnabas, Silas, Timothy, Lydia</p>
                    <p className="text-sm italic">Game Coverage: Level 3-6</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-purple-700 mb-3">Paul's Epistles</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Major Themes:</strong> Faith, grace, justification, sanctification, church unity, spiritual gifts</p>
                    <p><strong>Key Books & Verses:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li><strong>Romans:</strong> Salvation by faith (Romans 3:23, 6:23, 8:1, 10:9-10)</li>
                      <li><strong>1 Corinthians:</strong> Love chapter (1 Cor 13), Spiritual gifts (1 Cor 12)</li>
                      <li><strong>Galatians:</strong> Fruit of the Spirit (Galatians 5:22-23)</li>
                      <li><strong>Ephesians:</strong> Armor of God (Ephesians 6:10-18)</li>
                      <li><strong>Philippians:</strong> Joy and contentment (Philippians 4:4-13)</li>
                      <li><strong>Colossians:</strong> Supremacy of Christ</li>
                      <li><strong>1 & 2 Timothy, Titus:</strong> Pastoral letters on church leadership</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 4-6</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl">
                  <h3 className="text-xl font-semibold text-indigo-700 mb-3">General Epistles & Revelation</h3>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>General Epistles:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li><strong>Hebrews:</strong> Jesus as our High Priest, faith hall of fame (Heb 11)</li>
                      <li><strong>James:</strong> Faith and works, taming the tongue</li>
                      <li><strong>1 & 2 Peter:</strong> Hope, suffering, false teachers</li>
                      <li><strong>1, 2, 3 John:</strong> Love, truth, fellowship</li>
                      <li><strong>Jude:</strong> Contending for the faith</li>
                    </ul>
                    <p><strong>Revelation:</strong></p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Letters to seven churches (Rev 2-3)</li>
                      <li>Throne room vision (Rev 4-5)</li>
                      <li>Seals, trumpets, and bowls</li>
                      <li>New heaven and new earth (Rev 21-22)</li>
                    </ul>
                    <p className="text-sm italic">Game Coverage: Level 5-6</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Level-Specific Content */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Star className="w-6 h-6 text-yellow-600" />
                <h2 className="text-3xl font-bold text-gray-800">What to Study by Level</h2>
              </div>

              <div className="space-y-3">
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 rounded-xl border-l-4 border-green-600">
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Level 1 (Easy) - Focus Areas</h3>
                  <p className="text-gray-700">
                    Basic biblical facts, well-known stories, major figures. Study: Creation, Noah, Abraham, Moses, 
                    David, Jesus' birth and ministry basics, the twelve disciples, major miracles, crucifixion and 
                    resurrection basics.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-100 to-cyan-100 p-4 rounded-xl border-l-4 border-blue-600">
                  <h3 className="text-xl font-semibold text-blue-800 mb-2">Level 2 (Moderate) - Focus Areas</h3>
                  <p className="text-gray-700">
                    Stories with more detail, parables, judges, kings, Jesus' teachings. Study: Specific parables 
                    (Prodigal Son, Good Samaritan), Sermon on the Mount, judges like Samson and Gideon, kings of 
                    Israel and Judah, prophets' messages, Acts' major events.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-xl border-l-4 border-purple-600">
                  <h3 className="text-xl font-semibold text-purple-800 mb-2">Level 3 (Hard) - Focus Areas</h3>
                  <p className="text-gray-700">
                    Specific verses, prophecies, timelines, minor prophets. Study: Isaiah's prophecies, Daniel's 
                    visions, Psalms (especially 23, 91, 119), Proverbs wisdom, gospel harmony, Paul's conversion 
                    and journeys, Revelation's churches.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-xl border-l-4 border-yellow-600">
                  <h3 className="text-xl font-semibold text-yellow-800 mb-2">Levels 4-6 (Premium) - Focus Areas</h3>
                  <p className="text-gray-700">
                    Deep theology, epistles, complex narratives, cross-references. Study: Romans (justification, 
                    sanctification), theological concepts in Ephesians and Colossians, pastoral epistles, Hebrews' 
                    priesthood teaching, James' practical wisdom, John's letters on love and truth, Revelation symbolism.
                  </p>
                </div>
              </div>
            </section>

            {/* Study Resources */}
            <section className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-6 h-6 text-teal-600" />
                <h2 className="text-3xl font-bold text-gray-800">Recommended Study Resources</h2>
              </div>
              <div className="space-y-3 text-gray-700">
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-teal-700 mb-2">Study Bibles</h3>
                  <p>
                    Use a quality study Bible with commentary, cross-references, and maps. Popular options include 
                    ESV Study Bible, NIV Study Bible, and Life Application Study Bible.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-cyan-700 mb-2">Online Tools</h3>
                  <p>
                    BibleGateway.com for multiple translations and searches, Blue Letter Bible for word studies, 
                    YouVersion Bible app for daily reading plans.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl">
                  <h3 className="font-semibold text-blue-700 mb-2">Reference Books</h3>
                  <p>
                    Bible dictionary, concordance, atlas, and commentary set. These help with historical context 
                    and deeper understanding.
                  </p>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Continue Your Bible Journey</h2>
              <p className="text-gray-700 mb-4">
                This reference guide provides a roadmap for studying the biblical content covered in Mega Church 
                Game 2025. Use it alongside our Bible Study Tips page to maximize both your game performance and 
                your spiritual growth. Remember, the game is a fun tool for learning, but there's no substitute 
                for personal time in God's Word.
              </p>
              <p className="text-gray-700 mb-4">
                As you play through the levels and encounter different topics, take time to read the full passages 
                in your Bible. The questions in the game are designed to spark curiosity and drive you deeper into 
                Scripture. May your knowledge grow and your faith strengthen with every level you complete!
              </p>
              <p className="text-gray-700 font-semibold">
                "All Scripture is God-breathed and is useful for teaching, rebuking, correcting and training in 
                righteousness." - 2 Timothy 3:16
              </p>
            </section>

            {/* Call to Action */}
            <div className="text-center pt-8 border-t-2 border-gray-200 space-y-4">
              <Button
                onClick={() => navigate('/bible-study-tips')}
                variant="outline"
                className="px-6 py-4 text-base rounded-xl mr-4"
              >
                Bible Study Tips
              </Button>
              <Button
                onClick={() => navigate('/game-strategies')}
                variant="outline"
                className="px-6 py-4 text-base rounded-xl mr-4"
              >
                Game Strategies
              </Button>
              <Button
                onClick={() => navigate('/level-select')}
                className="bg-gradient-to-r from-[#10B981] to-[#06B6D4] text-white px-8 py-6 text-lg rounded-2xl hover:shadow-2xl transition-all"
              >
                Play the Game!
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
